import { View } from 'react-native';
import { Icon, Text, useTheme } from 'react-native-paper';
import { useTranslation } from 'react-i18next';
import { formatClock, formatDrivingDuration } from '../../../lib/drivingLimit';
import { stateTone } from './colors';
import { formatTimer } from './limits';
import type { DriverWorkStateSnapshot } from './types';
import { useElapsedSeconds } from './useElapsedSeconds';

export const STATE_ICONS = {
  resting: 'power-sleep',
  available: 'account-clock',
  driving: 'steering',
} as const;

/**
 * Current state (the same three as today) with its colour and a live timer.
 * Replaces the title line and the «Condução hoje» line of the status card on
 * the driver home. What counts for the limits is explained only in the
 * limits card («Como contamos»).
 */
export const WorkStateHeader = ({
  snapshot,
  large = false,
}: {
  snapshot: DriverWorkStateSnapshot;
  large?: boolean;
}) => {
  const { t } = useTranslation();
  const theme = useTheme();
  const tone = stateTone(snapshot.state, theme);
  const elapsed = useElapsedSeconds(snapshot.since);
  const since = formatClock(snapshot.since);
  const label = t(`workTime.states.${snapshot.state}`);

  return (
    <View testID="work-state-header">
      <View
        testID={`work-state-${snapshot.state}`}
        accessible
        accessibilityLabel={t('workTime.timerA11y', { state: label, duration: formatDrivingDuration(elapsed) })}
        style={{
          flexDirection: 'row',
          alignItems: 'center',
          gap: 12,
          padding: 12,
          borderRadius: 12,
          backgroundColor: tone.container,
        }}
      >
        <View
          style={{
            width: 44,
            height: 44,
            borderRadius: 22,
            alignItems: 'center',
            justifyContent: 'center',
            backgroundColor: theme.dark ? 'rgba(0, 0, 0, 0.22)' : 'rgba(255, 255, 255, 0.7)',
          }}
        >
          <Icon source={STATE_ICONS[snapshot.state]} size={24} color={tone.accent} />
        </View>
        <View style={{ flex: 1, minWidth: 0 }}>
          <Text variant={large ? 'headlineSmall' : 'titleLarge'} style={{ color: tone.onContainer, fontWeight: '700' }}>
            {label}
          </Text>
        </View>
        {snapshot.since ? (
          <View style={{ alignItems: 'flex-end' }}>
            <Text
              testID="work-state-timer"
              variant={large ? 'headlineMedium' : 'headlineSmall'}
              style={{ color: tone.onContainer, fontWeight: '700', fontVariant: ['tabular-nums'] }}
            >
              {formatTimer(elapsed)}
            </Text>
            {since ? (
              <Text variant="labelSmall" style={{ color: tone.onContainer }}>
                {t('workTime.since', { time: since })}
              </Text>
            ) : null}
          </View>
        ) : null}
      </View>
    </View>
  );
};
